import { Injectable } from '@jack-henry/frontend-utils/di';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { LocalStorageService, WindowService } from '@treasury/utils/services';
import { APP_THEME_STORAGE_KEY, AppTheme } from './theme.service.types';

@Injectable()
export class ThemeService {
    constructor(
        private storage: LocalStorageService,
        private windowService: WindowService
    ) {
        this.themeAttributeObserver.observe(document.documentElement, { attributes: true });
    }

    private readonly themeAttributeObserver = new MutationObserver(mutations => {
        for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'theme') {
                const value = (mutation.target as HTMLElement).getAttribute('theme');
                if (!value) {
                    return;
                }
                this.themeSource.emit(value as AppTheme);
            }
        }
    });

    private readonly themeSource = new ObservationSource<AppTheme>(({ emit }) => {
        const storedTheme = this.getStoredTheme();
        emit(this.resolveEffectiveTheme(storedTheme));
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly theme$ = this.themeSource.toObservable();

    private mediaQueryList?: MediaQueryList;

    private stopDevicePreferenceListener?: () => void;

    /**
     * Sets the preferred color scheme for the application based on stored preference or device settings.
     */
    public init() {
        const storedTheme = this.getStoredTheme();
        const effectiveTheme = this.resolveEffectiveTheme(storedTheme);

        document.documentElement.setAttribute('theme', effectiveTheme);

        if (!storedTheme || storedTheme === AppTheme.Auto) {
            this.startListeningToDevicePreference();
        }
    }

    public setTheme(theme: AppTheme) {
        this.storage.set(APP_THEME_STORAGE_KEY, theme);

        const effectiveTheme = this.resolveEffectiveTheme(theme);
        document.documentElement.setAttribute('theme', effectiveTheme);

        if (theme === AppTheme.Auto) {
            this.startListeningToDevicePreference();
        } else {
            this.stopListeningToDevicePreference();
        }
    }

    public getThemePreference(): AppTheme {
        return this.getStoredTheme() ?? AppTheme.Auto;
    }

    private getDevicePreference(): AppTheme.Light | AppTheme.Dark {
        if (!this.mediaQueryList) {
            this.mediaQueryList = this.windowService.matchMedia('(prefers-color-scheme: dark)');
        }
        return this.mediaQueryList.matches ? AppTheme.Dark : AppTheme.Light;
    }

    private resolveEffectiveTheme(theme: AppTheme | undefined): AppTheme.Light | AppTheme.Dark {
        if (!theme || theme === AppTheme.Auto) {
            return this.getDevicePreference();
        }
        return theme;
    }

    private startListeningToDevicePreference() {
        if (this.stopDevicePreferenceListener) {
            return;
        }

        const { unsubscribe } = this.windowService.addMediaQueryListener(
            '(prefers-color-scheme: dark)',
            () => {
                const storedTheme = this.getStoredTheme();
                if (!storedTheme || storedTheme === AppTheme.Auto) {
                    document.documentElement.setAttribute('theme', this.getDevicePreference());
                }
            }
        );

        this.stopDevicePreferenceListener = unsubscribe;
    }

    private stopListeningToDevicePreference() {
        this.stopDevicePreferenceListener?.();
        this.stopDevicePreferenceListener = undefined;
    }

    private getStoredTheme() {
        return this.storage.get<AppTheme>(APP_THEME_STORAGE_KEY);
    }
}
